import { Database, getDatabase, onValue, ref, serverTimestamp, update } from 'firebase/database'
import { firebaseApp } from '@contexts/firebase'
import {
    PlatformNotice,
    PlatformNoticeUserState,
    parsePlatformNoticeCollection,
    parsePlatformNoticeUserStates,
} from '@models/PlatformNotice'

export type Unsubscribe = () => void

export interface PlatformNoticesRealtimeServiceInterface {
    subscribeToNotices(
        organizationID: string | undefined,
        onChange: (notices: PlatformNotice[]) => void,
        onError: (error: Error) => void
    ): Unsubscribe
    subscribeToUserState(
        uid: string,
        onChange: (states: Record<string, PlatformNoticeUserState>) => void,
        onError: (error: Error) => void
    ): Unsubscribe
    markDismissed(uid: string, notice: PlatformNotice): Promise<void>
    markAcknowledged(uid: string, notice: PlatformNotice): Promise<void>
}

export const PlatformNoticePaths = {
    global: 'platformNotices/global',
    organization: (organizationID: string) => `platformNotices/organizations/${organizationID}`,
    userState: (uid: string) => `platformNoticeUserState/${uid}`,
    userNoticeState: (uid: string, noticeID: string) => `platformNoticeUserState/${uid}/${noticeID}`,
}

/**
 * Reads the notices qontact-backend publishes to RTDB. The control-board user is
 * already signed in to the same Firebase project, so no token exchange is needed.
 * Writes only touch the user's own state node (dismissals / acknowledgements).
 */
export class PlatformNoticesRealtimeService implements PlatformNoticesRealtimeServiceInterface {
    private readonly database: Database

    constructor(database: Database = getDatabase(firebaseApp)) {
        this.database = database
    }

    subscribeToNotices(
        organizationID: string | undefined,
        onChange: (notices: PlatformNotice[]) => void,
        onError: (error: Error) => void
    ): Unsubscribe {
        let general: PlatformNotice[] | undefined
        // Without an organization there is no second path to wait for.
        let organization: PlatformNotice[] | undefined = organizationID ? undefined : []
        // Emit only once both paths answered, so notices present at load arrive together
        // (otherwise org notices would look like real-time arrivals and get deferred).
        const emit = () => {
            if (general !== undefined && organization !== undefined) {
                onChange([...general, ...organization])
            }
        }

        const unsubscribers: Unsubscribe[] = [
            onValue(
                ref(this.database, PlatformNoticePaths.global),
                (snapshot) => {
                    general = parsePlatformNoticeCollection(snapshot.val(), 'general')
                    emit()
                },
                (error) => {
                    general = general ?? []
                    onError(error)
                    emit()
                }
            ),
        ]
        if (organizationID) {
            unsubscribers.push(onValue(
                ref(this.database, PlatformNoticePaths.organization(organizationID)),
                (snapshot) => {
                    organization = parsePlatformNoticeCollection(snapshot.val(), 'organization', organizationID)
                    emit()
                },
                (error) => {
                    organization = organization ?? []
                    onError(error)
                    emit()
                }
            ))
        }
        return () => unsubscribers.forEach((unsubscribe) => unsubscribe())
    }

    subscribeToUserState(
        uid: string,
        onChange: (states: Record<string, PlatformNoticeUserState>) => void,
        onError: (error: Error) => void
    ): Unsubscribe {
        return onValue(
            ref(this.database, PlatformNoticePaths.userState(uid)),
            (snapshot) => onChange(parsePlatformNoticeUserStates(snapshot.val())),
            onError
        )
    }

    async markDismissed(uid: string, notice: PlatformNotice): Promise<void> {
        await update(ref(this.database, PlatformNoticePaths.userNoticeState(uid, notice.id)), {
            dismissedVersion: notice.version,
            dismissedAt: serverTimestamp(),
        })
    }

    async markAcknowledged(uid: string, notice: PlatformNotice): Promise<void> {
        await update(ref(this.database, PlatformNoticePaths.userNoticeState(uid, notice.id)), {
            acknowledgedVersion: notice.version,
            acknowledgedAt: serverTimestamp(),
            ...(notice.organizationID ? { organizationID: notice.organizationID } : {}),
        })
    }
}
